import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { en, type TranslationKeys } from "./translations/en";
import { ru } from "./translations/ru";
import { lv } from "./translations/lv";
import { supabase } from "@/integrations/supabase/client";

export type Language = "en" | "ru" | "lv";

const translations: Record<Language, TranslationKeys> = { en, ru, lv };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
}

const LanguageContext = createContext<LanguageContextType>({
  language: "en",
  setLanguage: () => {},
  t: en,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem("app_language");
    if (stored === "en" || stored === "ru" || stored === "lv") return stored;
    return "en";
  });

  const setLanguage = useCallback(async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("app_language", lang);
    // Persist to profile if logged in
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await supabase.from("profiles").update({ language: lang }).eq("user_id", session.user.id);
    }
  }, []);

  // On mount, load language from profile if logged in
  useEffect(() => {
    const loadProfileLanguage = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data } = await supabase.from("profiles").select("language").eq("user_id", session.user.id).single();
        if (data?.language && (data.language === "en" || data.language === "ru" || data.language === "lv")) {
          setLanguageState(data.language as Language);
          localStorage.setItem("app_language", data.language);
        }
      }
    };
    loadProfileLanguage();
  }, []);

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
